import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SETTINGS = {
  heading_font: 'Italiana',
  body_font: 'Inter',
  show_works_year: false,
  show_works_medium: false,
  show_sculptures_year: false,
  show_sculptures_medium: false,
  bank_name: '',
  bank_account_holder: '',
  bank_account_number: '',
  bank_clabe: '',
  bank_swift: '',
  bank_reference_note: '',
  shipping_mx_free: true,
  shipping_international_rate: 150,
  shipping_international_currency: 'usd',
  paypal_link: '',
};

// All available fonts that should be loaded
const ALL_FONTS = [
  'Italiana',
  'Cormorant',
  'Bodoni+Moda:opsz,wght@6..96,400;6..96,500',
  'Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500',
  'Playfair+Display:wght@400;500;600',
  'EB+Garamond:wght@400;500',
  'DM+Serif+Display',
  'Cardo:wght@400;700',
  'Marcellus',
  'Inter:wght@300;400;500;600',
  'Space+Grotesk:wght@400;500;600',
  'Manrope:wght@300;400;500;600',
];

export const AVAILABLE_HEADING_FONTS = [
  'Italiana',
  'Cormorant',
  'Bodoni Moda',
  'Fraunces',
  'Playfair Display',
  'EB Garamond',
  'DM Serif Display',
  'Cardo',
  'Marcellus',
];

export const AVAILABLE_BODY_FONTS = [
  'Inter',
  'Space Grotesk',
  'Manrope',
  'EB Garamond',
];

// Inject Google Fonts <link>
const ensureFontsLoaded = () => {
  if (document.getElementById('site-fonts-link')) return;
  const link = document.createElement('link');
  link.id = 'site-fonts-link';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${ALL_FONTS.map(f => `family=${f}`).join('&')}&display=swap`;
  document.head.appendChild(link);
};

const applyFontVars = (settings) => {
  document.documentElement.style.setProperty('--font-heading', `'${settings.heading_font || 'Italiana'}', serif`);
  document.documentElement.style.setProperty('--font-body', `'${settings.body_font || 'Inter'}', sans-serif`);
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        const merged = { ...DEFAULT_SETTINGS, ...data };
        setSettings(merged);
        applyFontVars(merged);
      } else {
        applyFontVars(DEFAULT_SETTINGS);
      }
    } catch (err) {
      applyFontVars(DEFAULT_SETTINGS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    ensureFontsLoaded();
    applyFontVars(DEFAULT_SETTINGS);
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (newSettings) => {
    const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newSettings),
    });
    if (response.ok) {
      const data = await response.json();
      const merged = { ...DEFAULT_SETTINGS, ...data };
      setSettings(merged);
      applyFontVars(merged);
      return true;
    }
    return false;
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
};
